import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, EyeOff } from "lucide-react";
import { StatCard } from "./Common";
import { api, formatIDR } from "../lib/api";
import { rangeParams } from "../lib/range";
import { MASK, money, toggleAmounts, useShowAmounts } from "../lib/privacy";

export function useVendorSummary(range) {
  const [v, setV] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/vendor-income/summary", { params: rangeParams(range) }).then((r) => { if (live) setV(r.data); });
    return () => { live = false; };
  }, [range]);
  return v;
}

export function EyeToggle({ testId = "amount-eye-toggle" }) {
  const shown = useShowAmounts();
  const Icon = shown ? Eye : EyeOff;
  return (
    <button type="button" onClick={toggleAmounts} aria-label={shown ? "Sembunyikan angka" : "Tampilkan angka"} aria-pressed={shown} className="h-11 w-11 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid={testId}>
      <Icon className="h-5 w-5" />
    </button>
  );
}

const pct = (n) => `${n.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`;

export function ProfitCard({ v, driverTotal, className = "" }) {
  const shown = useShowAmounts();
  const has = v?.count > 0;
  const profit = has ? v.total - driverTotal : 0;
  const color = !shown ? "text-slate-200" : profit >= 0 ? "text-emerald-400" : "text-red-400";
  return (
    <div className={`rise relative overflow-hidden rounded-2xl p-5 border grain bg-slate-950 text-white border-slate-900 ${className}`} data-testid="profit-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-400">Sisa Keuntungan</p>
      {has ? (
        <>
          <p className={`font-num font-bold mt-2 text-2xl sm:text-3xl tracking-tight ${color}`} data-testid="profit-value">{shown ? `${profit < 0 ? "- " : ""}${formatIDR(Math.abs(profit))}` : MASK}</p>
          <div className="mt-3 space-y-1 text-xs text-slate-400">
            <p className="flex justify-between gap-3">Pendapatan Vendor: <span className="font-num text-slate-200" data-testid="profit-vendor">{money(v.total, shown)}</span></p>
            <p className="flex justify-between gap-3">Pendapatan Driver: <span className="font-num text-slate-200" data-testid="profit-driver">{money(driverTotal, shown)}</span></p>
            <p className="flex justify-between gap-3">Margin: <span className={`font-num ${color}`} data-testid="profit-margin">{shown ? pct((profit / v.total) * 100) : "••%"}</span></p>
          </div>
        </>
      ) : (
        <>
          <p className="font-num font-bold mt-2 text-2xl sm:text-3xl tracking-tight text-slate-400" data-testid="profit-value">Belum ada data</p>
          <Link to="/admin/vendor-income" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-400 hover:text-emerald-300 min-h-[32px]" data-testid="profit-add-link">Input Pendapatan Vendor <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </>
      )}
    </div>
  );
}

export function CashCards({ v, paid, unpaid }) {
  return (
    <>
      <StatCard label="Kas Saat Ini" value={v?.count > 0 ? formatIDR(v.total - paid) : "Belum ada data"} hint="Vendor − driver sudah dibayar" testId="admin-stat-cash" delay={280} />
      <StatCard label="Hutang ke Driver" value={formatIDR(unpaid)} hint="Pendapatan driver belum dibayar" testId="admin-stat-debt" delay={320} />
    </>
  );
}

export function VendorIncomeSection({ v }) {
  const shown = useShowAmounts();
  if (!v) return null;
  return (
    <div className="mb-8" data-testid="vendor-income-section">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1">
          <h2 className="font-heading text-lg font-bold text-slate-900">Pendapatan Vendor</h2>
          <EyeToggle />
        </div>
        <Link to="/admin/vendor-income" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 min-h-[44px] sm:min-h-0 flex items-center gap-1" data-testid="vendor-income-manage-link">Kelola <ArrowUpRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {v.per_vendor.map((r, i) => (
          <StatCard key={r.vendor} label={r.vendor} value={money(r.total, shown)} hint={`${r.count} entri`} testId={`vendor-total-${r.vendor.toLowerCase().replace(/\W+/g, "-")}`} delay={i * 40} />
        ))}
        <div className="rise rounded-2xl p-5 border bg-emerald-600 border-emerald-600 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-100">Total Gabungan</p>
          <p className="font-num font-bold mt-2 text-xl sm:text-2xl tracking-tight" data-testid="vendor-total-all">{money(v.total, shown)}</p>
          <p className="text-xs mt-1 text-emerald-100">{v.count} entri</p>
        </div>
      </div>
    </div>
  );
}
